import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { toast } from 'sonner';

import {
  electronApis,
  TranscriptionInputDeviceError,
} from '@/features/electron-api';
import {
  type PreferredInputDevice,
  getEffectiveInputDeviceDisplayName,
  getPreferredInputDeviceDisplayName,
  SYSTEM_DEFAULT_PREFERRED_INPUT_DEVICE,
} from '@/lib/meetings/transcription-input-device';

import {
  type DeviceLists,
  useDeviceLists,
} from './transcription-input-device.lists';
import { toOptionValue } from './transcription-input-device.options';

import type { TranscriptionInputDeviceConfig } from './transcription-input-device.types';

/** Warms a consumer's list before its dropdown mounts, so it opens with devices */
export function prefetchTranscriptionInputDevices(
  lists: DeviceLists,
  config: TranscriptionInputDeviceConfig
) {
  return lists.get(config.cacheKey).refresh();
}

function useTranscriptionInputDevicesState(cacheKey: string) {
  const list = useDeviceLists().get(cacheKey);
  const state = useSyncExternalStore(list.subscribe, list.read, list.read);

  const getDevices = useCallback(() => list.refresh(), [list]);

  useEffect(() => {
    getDevices();
    return electronApis.transcriptionInputDevice.onDeviceChanged((next) =>
      list.set(next)
    );
  }, [getDevices, list]);

  return {
    state,
    getDevices,
    isLoaded: state !== null,
  };
}

async function setInputDevice(selection: PreferredInputDevice) {
  try {
    await electronApis.transcriptionInputDevice.setDevice(selection);
  } catch (e) {
    const message =
      e instanceof TranscriptionInputDeviceError
        ? e.displayMessage
        : 'Failed to switch input device';

    toast.error(message);
  }
}

export function useTranscriptionInputDevice(
  config: TranscriptionInputDeviceConfig
) {
  const { cacheKey, displayMode, shouldApplyImmediately, storage } = config;
  const { state, getDevices, isLoaded } =
    useTranscriptionInputDevicesState(cacheKey);

  const selectionForDisplay: PreferredInputDevice =
    displayMode === 'saved_preference'
      ? storage.read()
      : (state?.selection ?? SYSTEM_DEFAULT_PREFERRED_INPUT_DEVICE);

  const selectDevice = useCallback(
    async (selection: PreferredInputDevice) => {
      storage.write(selection);

      if (!shouldApplyImmediately()) {
        return;
      }

      await setInputDevice(selection);
    },
    [shouldApplyImmediately, storage]
  );

  const selectedValue = toOptionValue(selectionForDisplay, state);

  const displayName =
    displayMode === 'live_effective'
      ? getEffectiveInputDeviceDisplayName(selectionForDisplay, state)
      : getPreferredInputDeviceDisplayName(selectionForDisplay, state);

  return {
    state,
    getDevices,
    isLoaded,
    selectedValue,
    displayName,
    selectDevice,
  };
}
